import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import * as ClientConstants from '../../../core/constants/client.constants';
import { ModelsService } from '../../../core/services/models/models.service';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { FiltersService } from '../../../core/services/declarations/filters.service';
import { IbanFormatPipe } from "../../../core/pipes/iban-format.pipe";
import { UtcDatePipe } from '../../../core/pipes/utc-date.pipe';
import { CommonModule } from '@angular/common';
import { DeclarationsService } from '../../../core/services/declarations/declarations.service';
import { ActivatedRoute } from '@angular/router';
import { ContractObservationsService } from '../../../core/services/contract-observations/contract-observations.service';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionService } from '../../../core/services/permission.service';
import { Rol } from '../../../core/enums/rol.enum';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';
import { faTrash, faXmark, faCommentPlus, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';

@Component({
  selector: 'app-declarations-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DropdownModule, InputTextModule, InputTextareaModule, CheckboxModule, CalendarModule, AccordionModule, ClientsTableComponent, IbanFormatPipe, UtcDatePipe, CommonModule, FontAwesomeModule, AutoAuditDirective],
  templateUrl: './declarations-form.component.html',
  styleUrl: './declarations-form.component.scss',
  host: {},
})
export class DeclarationsFormComponent implements OnChanges {

  faTrash = faTrash;
  faXmark = faXmark;
  faCommentPlus = faCommentPlus;
  faTriangleExclamation = faTriangleExclamation;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  @Input() clientId: any;
  @Input() hasEditPermission: boolean;
  @Input() snapshotData: any | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();
  @ViewChild('progressBar') progressBar: ElementRef;

  private lastClientId: any = null;

  public clientConstants = ClientConstants;

  declarationsForm = this.fb.group({
    declarations: this.fb.array([]),
  });

  departmentsTypes = ClientConstants.DEPARTMENTS_TYPES;
  isSaved = false;

  headers = [
    {
      name: 'Modelo',
      key: 'model'
    },
    {
      name: 'Responsable',
      key: 'responsible'
    },
    {
      name: 'Fecha alta',
      key: 'startDate'
    },
    {
      name: 'Fecha baja',
      key: 'endDate'
    },
    {
      name: 'Acciones',
      key: 'actions'
    },
    {
      name: '',
      key: 'observations'
    }];

  configTable = {
    headers: this.headers,
    data: undefined,
    users: [],
    isContracts: true,
    hasObservations: (contractId: number) => this.hasObservations(contractId),
    hasAvisos: (contractId: number) => this.hasAvisos(contractId),
    hasAnyObservations: (contractId: number) => this.hasAnyObservations(contractId)
  }

  get activeContractsTableConfig(): any {
    if (!this.snapshotData) return this.configTable;
    // In snapshot: keep observations column accessible, remove write actions
    return {
      ...this.configTable,
      headers: this.configTable.headers.filter(
        (h: any) => h.key !== 'actions',
      ),
    };
  }

  modalForm: FormGroup = this.fb.group({
    id: [],
    client: [],
    model: ['', Validators.required],
    presentationType: [''],
    bankAccount: [''],
    format: [''],
    responsible: [''],
    executors: this.fb.array([]),
    startDate: [],
    endDate: [],
    maxPhase: [],
    executeNextDeclaration: [false],
    // TODO: Uncomment when observations field is added in backend -h
    // observations: ['']
  });

  createExtraTemporaryModalForm: FormGroup = this.fb.group({
    model: [, Validators.required],
    period: [],
    trimester: [],
    month: [],
    year: [, Validators.required],
    generateAtPeriodStart: [false],
  });

  isExtraTemporaryModalActive = false;
  currentExtraTemporaryContractId: number = null;
  extraTemporaryFeedbackMessage = '';
  isObservationsModalActive = false;
  currentContractId: number = null;
  currentContractModelDepartment: string = null;
  observations: any[] = [];
  filteredObservations: any[] = [];
  currentUserId: number = null;
  currentUserDepartment: string = null;
  contractsWithObservations: Set<number> = new Set();
  contractsWithAvisos: Set<number> = new Set();
  observationsModalMode: 'observaciones' | 'avisos' | 'readonly' = 'observaciones';
  showDeletedAvisos = false;
  deletedAvisos: any[] = [];

  observationForm: FormGroup = this.fb.group({
    comment: ['', Validators.required],
    isNotification: [false]
  });

  fixedApplications: any[] = [
    { name: 'A3Soc', code: '' },
    { name: 'A3Ren', code: '' },
    { name: 'A3Nom', code: '' },
    { name: 'A3Eco', code: '' },
    { name: 'A3Ges', code: '' },
  ];

  fixedApplicationsForm: FormGroup = this.fb.group({
    id: [],
    clientId: [''],
    a3SocCode: [null, [Validators.pattern(/^\d*$/)]],
    a3RenCode: [null, [Validators.pattern(/^\d*$/)]],
    a3NomCode: [null, [Validators.pattern(/^\d*$/)]],
    a3EcoCode: [null, [Validators.pattern(/^\d*$/)]],
    a3GesCode: [null, [Validators.pattern(/^\d*$/)]]
  });

  declarationsNumber = 0;

  presentations: any[] = [];

  users: any[];
  filteredUsers: any[] = [];
  isLoading = false;
  fieldAuditData: LatestPerFieldMap = {};
  models: any[];
  bankAccounts: any;
  isDecModalActive: boolean;
  isCreating: boolean;
  availablePresentationsTypes: any;
  contracts: any = [];

  trimesters = [
    { label: '1T', value: 1 },
    { label: '2T', value: 2 },
    { label: '3T', value: 3 },
    { label: '4T', value: 4 }
  ];

  constructor(
    private fb: FormBuilder,
    private modelsService: ModelsService,
    private clientsService: ClientsService,
    private filtersService: FiltersService,
    private declarationsService: DeclarationsService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private contractObservationsService: ContractObservationsService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private auditService: AuditService,
  ) {
    this.declarationsForm.valueChanges.subscribe(() => {
      if (this.declarationsForm.dirty) {
        this.formIsDirty.emit(true);
      }
    });
  }

  isSavingContract = false;

  async ngOnInit() {
    this.isLoading = true;
    // const id = this.route.snapshot.paramMap.get('clientId') || this.route.snapshot.paramMap.get('id');
    // this.clientId = id && id !== 'new' ? Number(id) : null;
    this.currentUserId = this.authService.getUserId();
    const currentUser = this.authService.getUser();
    this.currentUserDepartment = currentUser?.user?.department;
    try {
      this.users = await this.filtersService.getUsers();
      this.configTable.users = this.users;
      this.models = await this.modelsService.getModelsGroupedByDepartment();
      if (this.models) {
        this.models.forEach(model => {
          model.department = this.getDepartmentLabel(model.department);
        });
      }
      if (this.clientId) {
        this.lastClientId = this.clientId;
        this.bankAccounts = (await this.clientsService.getClientBankAccounts(this.clientId)).filter((account: any) => account.endDate === null);
        await this.getData();
      }
    } finally {
      this.isLoading = false; // si falla un GET, no dejar el formulario en blanco para siempre
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    // Si el componente ya está inicializado y el clientId cambia o se detecta cualquier cambio
    if (this.lastClientId !== null && this.clientId) {
      // Recargar los indicadores de avisos y observaciones siempre que el componente se haga visible
      await this.refreshObservationsIndicators();
    }
  }

  async refreshObservationsIndicators() {
    // Este método recarga solo los indicadores sin recargar todos los datos
    // Si no hay contratos cargados, cargar todos los datos
    if (!this.contracts || this.contracts.length === 0) {
      if (this.clientId) {
        this.contracts = await this.clientsService.getContractsByClient(this.clientId);
      }
    }
    
    // Recargar indicadores
    if (this.contracts && this.contracts.length > 0) {
      await this.loadContractsWithObservations();
      await this.loadContractsWithAvisos();
    }
  }

  async getData() {
    try {
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadData() {
    this.isLoading = true
    let clientApps = await this.clientsService.getClientApplications(this.clientId);
    if (clientApps) {
      this.fixedApplicationsForm.patchValue({
        id: clientApps.id || null,
        client: clientApps.client || null,
        a3SocCode: clientApps.a3SocCode || null,
        a3RenCode: clientApps.a3RenCode || null,
        a3NomCode: clientApps.a3NomCode || null,
        a3EcoCode: clientApps.a3EcoCode || null,
        a3GesCode: clientApps.a3GesCode || null
      });
    }

    let decs = await this.clientsService.getContractsByClient(this.clientId);
    this.contracts = decs;
    
    // Cargar contratos que tienen observaciones y avisos
    await this.loadContractsWithObservations();
    await this.loadContractsWithAvisos();
    let declarationsArray = this.declarationsForm.get('declarations') as FormArray;
    // Limpiar el FormArray antes de agregar nuevos elementos
    while (declarationsArray.length !== 0) {
      declarationsArray.removeAt(0);
    }
    decs.forEach(declaration => {
      declaration.startDate = parseDateOnlyToLocal(declaration.startDate);
      declaration.endDate = parseDateOnlyToLocal(declaration.endDate);
      declaration.maxPhase = this.getMaxPhaseByModelId(declaration.model.id);
      declaration.bankAccount = declaration.bankAccount?.id;
      declaration.responsible = declaration.responsible?.id;

      const { executors, ...rest } = declaration;
      const group: any = this.fb.group({
        ...rest,
        executors: [],
        observations: [declaration.observations || '']
      });

      const executorsFA = this.fb.array([]);
      const maxPhase = declaration.maxPhase || 0;

      for (let i = 0; i < maxPhase; i++) {
        executorsFA.push(this.fb.control(executors?.[i] ?? null));
      }

      group.setControl('executors', executorsFA);
      declarationsArray.push(group);
    });
    this.declarationsNumber = decs.length;
    this.configTable.data = decs;
    this.isLoading = false

    try {
      this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_application');
    } catch {
      this.fieldAuditData = {};
    }
  }

  get declarations(): FormArray {
    return this.declarationsForm.get('declarations') as FormArray;
  }
  get executors(): FormArray {
    return (this.modalForm.get('executors') as FormArray);
  }

  getMaxPhaseByModelId(modelId: number): number | null {
    for (const dept of this.models) {
      const model = dept.models.find(m => m.id === modelId);
      if (model) {
        return model.maxPhase;
      }
    }

    return null;
  }

  addDeclaration() {
    const declaration = this.fb.group({
      id: [],
      client: [],
      model: ['', Validators.required],
      responsible: [''],
      executors: [],
      presentationType: ['', Validators.required],
      bankAccount: [''],
      format: [''],
      startDate: [],
      endDate: [],
      maxPhase: [],
      executeNextDeclaration: [false],
      observations: ['']
    });
    this.declarations.push(declaration);
  }

  onModelSelect(event) {
    this.filteredUsers = this.users.filter(user =>
      user.department === event.department || user.department === 'GENERAL'
    );
    const executorsArray = this.fb.array([]);
    for (let i = 0; i < event.maxPhase; i++) {
      executorsArray.push(this.fb.control(null));
    }
    this.modalForm.setControl('executors', executorsArray);

  }

  availablePresentations(event) {
    this.availablePresentationsTypes = event.value.availableTypePresentations;
  }

  async reopen(event) {
    const contractFromTable = this.configTable.data[event];
    const previousEndDate = contractFromTable.endDate;
    contractFromTable.endDate = null;
    let declarationsArray = this.declarationsForm.get('declarations') as FormArray;
    const contractIndex = declarationsArray.controls.findIndex(
      (control) => control.value.id === contractFromTable.id
    );
    if (contractIndex > -1) {
      let declaration = declarationsArray.at(contractIndex).value;
      declaration.endDate = null;
      delete declaration.maxPhase;
      try {
        await this.clientsService.saveClientContract({
          ...declaration,
          startDate: toDateOnlyPayload(declaration.startDate),
          endDate: null,
        });
      } catch (error) {
        contractFromTable.endDate = previousEndDate; // el servidor no lo reabrió: no mostrarlo abierto
        throw error;
      }
    }
  }

  async close(event) {
    const contractFromTable = this.configTable.data[event[0]];
    const previousEndDate = contractFromTable.endDate;
    contractFromTable.endDate = new Date();
    let declarationsArray = this.declarationsForm.get('declarations') as FormArray;
    const contractIndex = declarationsArray.controls.findIndex(
      (control) => control.value.id === contractFromTable.id
    );
    if (contractIndex > -1) {
      let declaration = declarationsArray.at(contractIndex).value;
      declaration.endDate = new Date();
      declaration.executeNextDeclaration = event[1];
      delete declaration.maxPhase;
      try {
        await this.clientsService.saveClientContract({
          ...declaration,
          startDate: toDateOnlyPayload(declaration.startDate),
          endDate: toDateOnlyPayload(declaration.endDate),
        });
      } catch (error) {
        contractFromTable.endDate = previousEndDate; // el servidor no lo cerró: no mostrarlo cerrado
        throw error;
      }
    }
  }

  openFormModal(index: number | null = null) {
    this.isDecModalActive = true;

    if (index === null) {
      this.isCreating = true;
      this.modalForm.reset();
    } else {
      this.isCreating = false;
      // Usar el ID del contrato de configTable.data para encontrar el contrato correcto
      const contractFromTable = this.configTable.data[index];
      const contractIndex = this.declarations.controls.findIndex(
        (control) => control.value.id === contractFromTable.id
      );
      const contract = contractIndex > -1 
        ? this.declarations.at(contractIndex).value 
        : contractFromTable;
      this.filteredUsers = this.users.filter(user =>
        user.department === contract.model.department || user.department === 'GENERAL'

      );
      this.availablePresentationsTypes = contract.model.availableTypePresentations;
      const executorsArray = this.fb.array(
        contract.executors ? contract.executors.map(executor => this.fb.control(executor)) : []
      );

      this.modalForm.patchValue({
        id: contract.id,
        presentationType: contract.presentationType,
        maxPhase: contract.maxPhase,
        model: contract.model,
        bankAccount: contract.bankAccount,
        responsible: contract.responsible,
        startDate: contract.startDate,
        endDate: contract.endDate,
        executeNextDeclaration: contract.executeNextDeclaration,
        observations: contract.observations || ''
      });
      this.modalForm.setControl('executors', executorsArray);

      if (this.snapshotData) {
        this.modalForm.disable();
        const executorsArr = this.modalForm.get('executors') as FormArray;
        executorsArr?.controls.forEach(c => c.disable({ emitEvent: false }));
      } else if (!this.hasEditPermission) {
        Object.keys(this.modalForm.controls).forEach((control) => {
          this.modalForm.get(control)?.disable({ emitEvent: false });
        });

        const executorsArray = this.modalForm.get('executors') as FormArray;
        executorsArray.controls.forEach(control => control.disable({ emitEvent: false }));
      }

    }
  }

  async save() {
    if (this.isSavingContract) return; // doble clic = dos contratos
    this.isSavingContract = true;
    try {
      await this.saveContract();
    } finally {
      this.isSavingContract = false;
    }
  }

  private async saveContract() {
    const index = this.declarations.controls.findIndex(
      (control) => control.value.id === this.modalForm.value.id
    );
    if (index > -1) {
      this.declarations.at(index).patchValue(this.modalForm.value);
      this.declarations.at(index).value.client = Number(this.clientId);
      let contract = { ...this.declarations.at(index).value };
      delete contract.maxPhase;
      contract.observations = this.modalForm.value.observations || '';
      await this.clientsService.saveClientContract({
        ...contract,
        startDate: toDateOnlyPayload(contract.startDate),
        endDate: toDateOnlyPayload(contract.endDate),
      });
    } else {
      let contract = { ...this.modalForm.value };
      contract.client = Number(this.clientId);
      delete contract.maxPhase;
      contract.observations = contract.observations || '';
      await this.clientsService.saveClientContract({
        ...contract,
        startDate: toDateOnlyPayload(contract.startDate),
        endDate: toDateOnlyPayload(contract.endDate),
      });
    }
    await this.getData();
    this.isDecModalActive = false;
  }

  getDepartmentLabel(departmentValue: string): string {
    const department = this.departmentsTypes.find(dep => dep.value === departmentValue);
    return department ? department.label : '';
  }

  async onSubmitFixedApplications() {
    const formValue = this.fixedApplicationsForm.value;
    const payload = {
      id: formValue.id || null,
      client: this.clientId,
      a3SocCode: formValue.a3SocCode || null,
      a3RenCode: formValue.a3RenCode || null,
      a3NomCode: formValue.a3NomCode || null,
      a3EcoCode: formValue.a3EcoCode || null,
      a3GesCode: formValue.a3GesCode || null
    };
    if (payload.id === null) {
      delete payload.id;
    }
    let clientApps = await this.clientsService.getClientApplications(this.clientId);

    if (clientApps && clientApps.id) {
      await this.clientsService.updateClientApplication(clientApps.id, payload);
    } else {
      await this.clientsService.createClientApplication(payload);
    }

    await this.getData();
    this.fixedApplicationsForm.markAsPristine();
  }

  openExtraTemporaryModal(index) {
    this.createExtraTemporaryModalForm.reset({ generateAtPeriodStart: false });
    this.currentExtraTemporaryContractId = this.contracts[index].id;
    this.extraTemporaryFeedbackMessage = '';
    this.createExtraTemporaryModalForm.patchValue({
      model: this.contracts[index].model
    });
    this.isExtraTemporaryModalActive = true;

  }

  closeExtraTemporaryModal() {
    this.createExtraTemporaryModalForm.reset({ generateAtPeriodStart: false });
    this.currentExtraTemporaryContractId = null;
    this.extraTemporaryFeedbackMessage = '';
    this.isExtraTemporaryModalActive = false;

  }

  async createExtraTemporaryDeclaration() {
    let dec = this.createExtraTemporaryModalForm.value;

    let startDate: Date;

    if (dec.model?.periodicity === 'TRIMESTRAL' && dec.trimester && dec.year) {
      const year = dec.year instanceof Date ? dec.year.getFullYear() : dec.year;    
      const month = (dec.trimester - 1) * 3;
      startDate = new Date(year, month, 1, 12); 
    } else if (dec.model?.periodicity === 'MONTHLY' && dec.month && dec.year) {
      const year = dec.year instanceof Date ? dec.year.getFullYear() : dec.year;
      const month = dec.month instanceof Date ? dec.month.getMonth() : dec.month - 1;
      startDate = new Date(year, month, 1, 12);
    } else if (dec.model?.periodicity === 'PERIOD' && dec.period) {
      const period = dec.model.customPeriods?.find(p => p.value === dec.period);
      startDate = period?.startDate ? new Date(new Date(period.startDate).setHours(12,0,0,0)) : new Date(new Date().setHours(12,0,0,0));
    } else if (dec.year) {
      const year = dec.year instanceof Date ? dec.year.getFullYear() : dec.year;
      startDate = new Date(year, 0, 1, 12);
    } else {
      startDate = new Date();
    }

    let newDeclaration = {
      client: this.clientId,
      model: dec.model.id,
      contractId: this.currentExtraTemporaryContractId,
      startDate: startDate,
      type: 'NORMAL',
      generateNow: !(dec.generateAtPeriodStart ?? false),
    }

    try {
      const response = await this.declarationsService.createExtraTemporaryDeclaration(newDeclaration);
      this.extraTemporaryFeedbackMessage = response?.status === 'PENDING'
        ? 'La declaración se generará al inicio del periodo seleccionado.'
        : 'Declaración creada correctamente.';
      this.isSaved = true;
      let width = 0;
      const interval = setInterval(() => {
        if (width >= 100) {
          clearInterval(interval);
          this.isSaved = false;
        } else {
          width++;
          this.renderer.setProperty(this.progressBar.nativeElement, 'value', width);
        }
      }, 30);
    } catch {
      this.extraTemporaryFeedbackMessage = 'No se pudo crear la declaración.';
    }

    this.createExtraTemporaryModalForm.reset({ generateAtPeriodStart: false });
    this.currentExtraTemporaryContractId = null;
    this.isExtraTemporaryModalActive = false;

  }

  currentContractName: string = '';
  currentClientName: string = '';

  async openObservationsModal(index: number) {
    this.observationsModalMode = this.snapshotData ? 'readonly' : 'observaciones';
    const contractFromTable = this.configTable.data[index];
    this.currentContractId = contractFromTable.id;
    this.currentContractModelDepartment = contractFromTable.model?.department;
    this.currentContractName = contractFromTable.model?.name || 'Contrato';
    // Obtener el nombre del cliente
    const client = await this.clientsService.getClientById(this.clientId);
    if (client) {
      const apellidos = `${client.firstSurname || ''} ${client.secondSurname || ''}`.trim();
      const nombre = client.name || '';
      this.currentClientName = apellidos && nombre ? `${apellidos}, ${nombre}` : apellidos || nombre;
    } else {
      this.currentClientName = '';
    }
    await this.loadObservations();
    this.filterObservations();
    this.observationForm.reset({ isNotification: false });
    this.isObservationsModalActive = true;
  }

  async openAvisosModal(index: number) {
    this.observationsModalMode = this.snapshotData ? 'readonly' : 'avisos';
    const contractFromTable = this.configTable.data[index];
    this.currentContractId = contractFromTable.id;
    this.currentContractModelDepartment = contractFromTable.model?.department;
    this.currentContractName = contractFromTable.model?.name || 'Contrato';
    // Obtener el nombre del cliente
    const client = await this.clientsService.getClientById(this.clientId);
    if (client) {
      const apellidos = `${client.firstSurname || ''} ${client.secondSurname || ''}`.trim();
      const nombre = client.name || '';
      this.currentClientName = apellidos && nombre ? `${apellidos}, ${nombre}` : apellidos || nombre;
    } else {
      this.currentClientName = '';
    }
    await this.loadObservations();
    this.filterObservations();
    this.observationForm.reset({ isNotification: false });
    this.isObservationsModalActive = true;
  }

  async openAllObservationsModal(index: number) {
    this.observationsModalMode = 'readonly';
    const contractFromTable = this.configTable.data[index];
    this.currentContractId = contractFromTable.id;
    this.currentContractModelDepartment = contractFromTable.model?.department;
    this.currentContractName = contractFromTable.model?.name || 'Contrato';
    // Obtener el nombre del cliente
    const client = await this.clientsService.getClientById(this.clientId);
    if (client) {
      const apellidos = `${client.firstSurname || ''} ${client.secondSurname || ''}`.trim();
      const nombre = client.name || '';
      this.currentClientName = apellidos && nombre ? `${apellidos}, ${nombre}` : apellidos || nombre;
    } else {
      this.currentClientName = '';
    }
    await this.loadObservations();
    this.filterObservations();
    this.isObservationsModalActive = true;
  }

  closeObservationsModal() {
    this.isObservationsModalActive = false;
    this.currentContractId = null;
    this.currentContractModelDepartment = null;
    this.currentContractName = '';
    this.currentClientName = '';
    this.observations = [];
    this.filteredObservations = [];
    this.deletedAvisos = [];
    this.showDeletedAvisos = false;
    this.observationForm.reset();
    this.observationsModalMode = 'observaciones';
  }

  filterObservations() {
    if (this.observationsModalMode === 'avisos') {
      this.filteredObservations = this.observations.filter(obs => obs.isNotification === true);
    } else if (this.observationsModalMode === 'readonly') {
      this.filteredObservations = [...this.observations];
    } else {
      this.filteredObservations = this.observations.filter(obs => !obs.isNotification);
    }
  }

  async loadObservations() {
    if (this.currentContractId) {
      try {
        this.observations = await this.contractObservationsService.getObservationsByContract(this.currentContractId);
      } catch (error) {
        console.error('Error loading observations:', error);
        this.observations = [];
      }
    }
  }

  async toggleDeletedAvisos() {
    this.showDeletedAvisos = !this.showDeletedAvisos;
    if (this.showDeletedAvisos && this.deletedAvisos.length === 0 && this.currentContractId) {
      try {
        const log = await this.contractObservationsService.getObservationsByContractLog(this.currentContractId);
        this.deletedAvisos = (log || []).filter((obs: any) => obs.deleted_at);
      } catch (error) {
        console.error('Error loading deleted avisos:', error);
        this.deletedAvisos = [];
      }
    }
  }

  async addObservation() {
    
    if (this.observationForm.valid && this.currentContractId && this.currentUserId) {
      try {
        const isNotificationValue = this.observationsModalMode === 'avisos';
        
        const observationData = {
          contractId: this.currentContractId,
          userId: this.currentUserId,
          comment: this.observationForm.value.comment,
          isNotification: isNotificationValue
        };
        
        await this.contractObservationsService.createObservation(observationData);
        await this.loadObservations();
        this.filterObservations();
        await this.loadContractsWithObservations();
        await this.loadContractsWithAvisos();
        this.observationForm.reset({ isNotification: false });
      } catch (error) {
        console.error('Error adding observation:', error);
      }
    } else {
      console.warn('Form validation failed or missing data');
      if (!this.observationForm.valid) console.warn('Form is invalid');
      if (!this.currentContractId) console.warn('Contract ID is missing');
      if (!this.currentUserId) console.warn('User ID is missing');
    }
  }

  async deleteObservation(observationId: number) {
    try {
      await this.contractObservationsService.deleteObservation(observationId);
      await this.loadObservations();
      this.filterObservations();
      // Refresh deleted list if it was already loaded
      if (this.showDeletedAvisos && this.currentContractId) {
        const log = await this.contractObservationsService.getObservationsByContractLog(this.currentContractId);
        this.deletedAvisos = (log || []).filter((obs: any) => obs.deleted_at);
      }
      await this.loadContractsWithObservations();
      await this.loadContractsWithAvisos();
    } catch (error) {
      console.error('Error deleting observation:', error);
    }
  }

  async loadContractsWithObservations() {
    this.contractsWithObservations.clear();
    for (const contract of this.contracts) {
      try {
        const obs = await this.contractObservationsService.getObservationsByContract(contract.id);
        if (obs && obs.length > 0) {
          const hasNonNotifications = obs.some(o => !o.isNotification);
          if (hasNonNotifications) {
            this.contractsWithObservations.add(contract.id);
          }
        }
      } catch (error) {
        console.error(`Error loading observations for contract ${contract.id}:`, error);
      }
    }
  }

  hasObservations(contractId: number): boolean {
    return this.contractsWithObservations.has(contractId);
  }

  async loadContractsWithAvisos() {
    this.contractsWithAvisos.clear();
    for (const contract of this.contracts) {
      try {
        const obs = await this.contractObservationsService.getObservationsByContract(contract.id);
        if (obs && obs.length > 0) {
          const hasAvisos = obs.some(o => o.isNotification === true);
          if (hasAvisos) {
            this.contractsWithAvisos.add(contract.id);
          }
        }
      } catch (error) {
        console.error(`Error loading avisos for contract ${contract.id}:`, error);
      }
    }
  }

  hasAvisos(contractId: number): boolean {
    return this.contractsWithAvisos.has(contractId);
  }

  hasAnyObservations(contractId: number): boolean {
    return this.hasObservations(contractId) || this.hasAvisos(contractId);
  }

  canDeleteAviso(): boolean {
    if (this.permissionService.hasRole(Rol.Admin)) {
      return true;
    }
    return this.currentUserDepartment === this.currentContractModelDepartment;
  }
}
